import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import { alsokTheme, componentThemes } from '../styles/alsok-theme'
import { LipSyncManager, createSimpleLipSync } from '../utils/lipSync'
import { ExpressionManager } from '../utils/expressionManager'

const Avatar = forwardRef(({ status, message, isListening, isSpeaking, audioElement }, ref) => {
  const [animation, setAnimation] = useState('idle')
  const [mouthOpenness, setMouthOpenness] = useState(0)
  const [expression, setExpression] = useState('neutral')
  const [facialExpression, setFacialExpression] = useState(ExpressionManager.expressions.neutral)
  const lipSyncRef = useRef(null)
  const audioRef = useRef(null)
  const expressionManagerRef = useRef(null)
  
  useEffect(() => {
    if (isSpeaking) {
      setAnimation('speaking')
      setExpression('speaking')
    } else if (isListening) {
      setAnimation('listening')
      setExpression('attentive')
    } else {
      setAnimation('idle')
      setExpression('neutral')
      setMouthOpenness(0)
    }
  }, [isListening, isSpeaking])
  
  // リップシンクと表情管理の初期化
  useEffect(() => {
    const initialize = async () => {
      // リップシンク初期化
      if (typeof window !== 'undefined' && window.AudioContext) {
        lipSyncRef.current = new LipSyncManager()
        await lipSyncRef.current.initializeAudioContext()
      }
      
      // 表情管理初期化
      expressionManagerRef.current = new ExpressionManager()
    }
    
    initialize()
    
    return () => {
      if (lipSyncRef.current) {
        lipSyncRef.current.destroy()
      }
      if (expressionManagerRef.current) {
        expressionManagerRef.current.destroy()
      }
    }
  }, [])
  
  // メッセージ変更時の表情更新
  useEffect(() => {
    if (message && expressionManagerRef.current) {
      const emotion = expressionManagerRef.current.analyzeTextEmotion(message)
      const newExpression = expressionManagerRef.current.getExpressionFromEmotion(emotion)
      const facialData = expressionManagerRef.current.setExpression(newExpression)
      
      setFacialExpression(facialData)
      setExpression(newExpression)
    }
  }, [message])
  
  // 音声再生時のリップシンク設定
  const handleAudioPlay = (audioElement) => {
    if (lipSyncRef.current && audioElement) {
      // 高度なリップシンクが利用可能な場合
      lipSyncRef.current.attachToAudio(audioElement, (openness) => {
        setMouthOpenness(openness)
      })
    } else {
      // シンプルなリップシンクにフォールバック
      createSimpleLipSync(audioElement, setMouthOpenness)
    }
  }
  
  // 外部から音声要素を受け取るためのコールバック
  const setAudioElement = (audio) => {
    audioRef.current = audio
    if (audio && isSpeaking) {
      handleAudioPlay(audio)
    }
  }
  
  // 外部からアクセス可能なメソッドを公開
  useImperativeHandle(ref, () => ({
    setAudioElement,
    setExpression: (expressionName) => {
      if (expressionManagerRef.current) {
        const facialData = expressionManagerRef.current.setExpression(expressionName)
        setFacialExpression(facialData)
        setExpression(expressionName)
      }
    },
    playExpressionSequence: (sequence) => {
      if (expressionManagerRef.current) {
        expressionManagerRef.current.expressionQueue = sequence
        expressionManagerRef.current.playExpressionQueue((expression) => {
          setFacialExpression(expression)
        })
      }
    }
  }))
  
  // audioElementプロパティが変更された時の処理
  useEffect(() => {
    if (audioElement && isSpeaking) {
      handleAudioPlay(audioElement)
    }
  }, [audioElement, isSpeaking])
  
  const getAvatarClass = () => {
    return `avatar avatar-${animation} ${status}`
  }
  
  const getReceptionistMessage = () => {
    switch (status) {
      case 'ready': return '😊 いらっしゃいませ！ALSOK受付の佐藤です。本日はお忙しい中お越しいただきありがとうございます。ご用件をお伺いします。'
      case 'processing': return '😊 かしこまりました。確認いたしますので、少々お待ちください。'
      case 'listening': return '👂 はい、お聞きしております。どうぞお話しください。'
      case 'speaking': return '💬 ご案内させていただきます。'
      case 'error': return '😔 申し訳ございません。もう一度お試しいただけますでしょうか。'
      default: return '😊 いらっしゃいませ！ALSOK受付の佐藤です。ご用件をお伺いします。'
    }
  }
  
  return (
    <div className="avatar-container">
      <div className="avatar-wrapper">
        <div className={getAvatarClass()}>
          <div className="avatar-face">
            <div className="receptionist-avatar">
              <div className="face">
                <div className="hair"></div>
                <div 
                  className="eyebrows"
                  style={{
                    transform: `translateY(${facialExpression.eyebrows * -2}px)`,
                    transition: 'transform 0.3s ease-out'
                  }}
                ></div>
                <div className="face-base">
                  <div className="eyes">
                    <div 
                      className="eye left-eye" 
                      style={{
                        transform: `scaleY(${facialExpression.eyes}) scaleX(1)`,
                        transition: 'transform 0.3s ease-out'
                      }}
                    >
                      <div 
                        className="pupil"
                        style={{
                          transform: `translateX(${facialExpression.pupils * 2}px)`,
                          transition: 'transform 0.3s ease-out'
                        }}
                      ></div>
                    </div>
                    <div 
                      className="eye right-eye"
                      style={{
                        transform: `scaleY(${facialExpression.eyes}) scaleX(1)`,
                        transition: 'transform 0.3s ease-out'
                      }}
                    >
                      <div 
                        className="pupil"
                        style={{
                          transform: `translateX(${facialExpression.pupils * 2}px)`,
                          transition: 'transform 0.3s ease-out'
                        }}
                      ></div>
                    </div>
                  </div>
                  <div className="nose"></div>
                  <div 
                    className="mouth" 
                    style={{
                      transform: `scaleY(${1 + mouthOpenness}) translateY(${facialExpression.mouth * 2}px)`,
                      height: `${4 + mouthOpenness * 8}px`,
                      width: `${8 + mouthOpenness * 6}px`,
                      borderColor: facialExpression.mouth > 0.5 ? '#ff6b9d' : '#ff8fa3',
                      borderRadius: facialExpression.mouth > 0.5 ? '50%' : '0 0 8px 8px'
                    }}
                  ></div>
                </div>
              </div>
              <div className="body">
                <div className="neck"></div>
                <div className="clothing">
                  <div className="uniform"></div>
                  <div className="name-tag">ALSOK</div>
                </div>
              </div>
            </div>
          </div>
          
          {message && (
            <div className="avatar-message-overlay">
              <div className="message-bubble">
                {message}
              </div>
            </div>
          )}
          
          <div className="avatar-pulse"></div>
        </div>
      </div>
      
      <style jsx="true">{`
        .avatar-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 20px;
        }
        
        .avatar-wrapper {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .avatar {
          position: relative;
          width: 160px;
          height: 160px;
          border-radius: ${alsokTheme.borderRadius.full};
          background: ${componentThemes.avatar.background};
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: ${alsokTheme.shadows.security};
          transition: ${alsokTheme.animation.transition};
          border: 4px solid ${alsokTheme.primary.light};
        }
        
        .avatar-idle {
          animation: float 3s ease-in-out infinite;
        }
        
        .avatar-idle .face {
          animation: gentle-nod 4s ease-in-out infinite;
        }
        
        .avatar-listening {
          animation: pulse 1s ease-in-out infinite;
          background: linear-gradient(135deg, ${alsokTheme.status.info} 0%, ${alsokTheme.primary.light} 100%);
          border-color: ${alsokTheme.status.info};
        }
        
        .avatar-listening .eyes {
          animation: listening-eyes 1.5s ease-in-out infinite;
        }
        
        .avatar-speaking {
          animation: bounce 0.5s ease-in-out infinite;
          background: linear-gradient(135deg, ${alsokTheme.accent.green} 0%, #38f9d7 100%);
          border-color: ${alsokTheme.accent.green};
        }
        
        .avatar-speaking .mouth {
          animation: speaking-mouth 0.3s ease-in-out infinite;
        }
        
        .avatar.processing {
          animation: spin 2s linear infinite;
          background: linear-gradient(135deg, ${alsokTheme.status.processing} 0%, ${alsokTheme.accent.yellow} 100%);
          border-color: ${alsokTheme.status.processing};
        }
        
        .avatar.error {
          background: linear-gradient(135deg, ${alsokTheme.status.error} 0%, #fad0c4 100%);
          border-color: ${alsokTheme.status.error};
        }
        
        .avatar-face {
          width: 120px;
          height: 120px;
          background: ${componentThemes.avatar.face};
          border-radius: ${alsokTheme.borderRadius.full};
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: ${alsokTheme.shadows.inset};
          border: 3px solid rgba(255,255,255,0.9);
          overflow: hidden;
        }
        
        .receptionist-avatar {
          width: 100%;
          height: 100%;
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        
        .face {
          position: relative;
          width: 70px;
          height: 65px;
          margin-bottom: 12px;
        }
        
        .hair {
          position: absolute;
          top: -8px;
          left: 5px;
          width: 60px;
          height: 40px;
          background: linear-gradient(135deg, #4a3728 0%, #6b4d3a 30%, #3d2820 100%);
          border-radius: 30px 30px 8px 8px;
          z-index: 1;
          box-shadow: inset 0 3px 8px rgba(0,0,0,0.3), 0 2px 6px rgba(0,0,0,0.2);
        }
        
        .hair::before {
          content: '';
          position: absolute;
          top: 15px;
          left: -2px;
          width: 12px;
          height: 20px;
          background: linear-gradient(45deg, #4a3728 0%, #5a4232 100%);
          border-radius: 6px 12px 8px 4px;
          transform: rotate(-15deg);
        }
        
        .hair::after {
          content: '';
          position: absolute;
          top: 15px;
          right: -2px;
          width: 12px;
          height: 20px;
          background: linear-gradient(45deg, #4a3728 0%, #5a4232 100%);
          border-radius: 12px 6px 4px 8px;
          transform: rotate(15deg);
        }
        
        
        .eyebrows {
          position: absolute;
          top: 5px;
          left: 50%;
          transform: translateX(-50%);
          width: 36px;
          height: 4px;
          z-index: 3;
        }
        
        .eyebrows::before {
          content: '';
          position: absolute;
          left: 2px;
          top: 0;
          width: 12px;
          height: 2px;
          background: #2c1810;
          border-radius: 2px;
          transform: rotate(-5deg);
        }
        
        .eyebrows::after {
          content: '';
          position: absolute;
          right: 2px;
          top: 0;
          width: 12px;
          height: 2px;
          background: #2c1810;
          border-radius: 2px;
          transform: rotate(5deg);
        }
        
        .face-base {
          width: 50px;
          height: 45px;
          background: linear-gradient(135deg, #ffdbac 0%, #ffd4a3 20%, #ffcfa0 100%);
          border-radius: 50% 50% 48% 52%;
          position: relative;
          z-index: 2;
          margin: 0 auto;
          box-shadow: inset 0 2px 4px rgba(255,255,255,0.3), inset 0 -1px 3px rgba(0,0,0,0.1);
        }
        
        .face-base::before {
          content: '';
          position: absolute;
          top: 20px;
          left: 8px;
          width: 8px;
          height: 6px;
          background: rgba(255, 182, 150, 0.4);
          border-radius: 50%;
          box-shadow: 26px 0 0 rgba(255, 182, 150, 0.4);
        }
        
        .eyes {
          display: flex;
          justify-content: space-between;
          margin-top: 12px;
          padding: 0 10px;
        }
        
        .eye {
          width: 10px;
          height: 8px;
          background: linear-gradient(135deg, #ffffff 0%, #f8f8f8 100%);
          border-radius: 60% 40% 40% 60%;
          position: relative;
          animation: blink 4s ease-in-out infinite;
          box-shadow: inset 0 1px 2px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.1);
        }
        
        .eye::before {
          content: '';
          position: absolute;
          top: -2px;
          left: 1px;
          width: 8px;
          height: 3px;
          background: linear-gradient(135deg, #8b4513 0%, #a0522d 100%);
          border-radius: 0 0 4px 4px;
        }
        
        .eye::after {
          content: '';
          position: absolute;
          bottom: -1px;
          left: 2px;
          width: 6px;
          height: 1px;
          background: rgba(139, 69, 19, 0.6);
          border-radius: 50%;
        }
        
        .pupil {
          width: 6px;
          height: 6px;
          background: radial-gradient(circle at 30% 30%, #4a5568 0%, #2d3748 60%, #1a202c 100%);
          border-radius: 50%;
          position: absolute;
          top: 1px;
          left: 2px;
          box-shadow: inset 1px 1px 2px rgba(255,255,255,0.3);
        }
        
        .pupil::before {
          content: '';
          position: absolute;
          top: 1px;
          left: 1px;
          width: 2px;
          height: 2px;
          background: rgba(255,255,255,0.8);
          border-radius: 50%;
        }
        
        .nose {
          width: 3px;
          height: 4px;
          background: linear-gradient(135deg, #ffb894 0%, #ff9d7a 100%);
          border-radius: 50% 50% 30% 30%;
          position: absolute;
          top: 22px;
          left: 50%;
          transform: translateX(-50%);
          box-shadow: 0 1px 2px rgba(0,0,0,0.2), inset 0 1px 1px rgba(255,255,255,0.3);
        }
        
        .nose::before {
          content: '';
          position: absolute;
          bottom: 0;
          left: -1px;
          width: 5px;
          height: 2px;
          background: rgba(255, 157, 122, 0.3);
          border-radius: 50%;
        }
        
        .mouth {
          width: 12px;
          height: 6px;
          border: 2px solid #d4426a;
          border-top: 1px solid #e85d7a;
          border-radius: 6px;
          position: absolute;
          top: 28px;
          left: 50%;
          transform: translateX(-50%);
          transition: all 0.1s ease-out;
          background: linear-gradient(135deg, #ff8fa3 0%, #ff6b9d 100%);
          box-shadow: inset 0 1px 2px rgba(255,255,255,0.4), 0 1px 2px rgba(0,0,0,0.1);
        }
        
        .mouth::before {
          content: '';
          position: absolute;
          top: 1px;
          left: 2px;
          width: 8px;
          height: 2px;
          background: rgba(255,255,255,0.6);
          border-radius: 50%;
        }
        
        .body {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .neck {
          width: 12px;
          height: 8px;
          background: linear-gradient(135deg, #ffdbac 0%, #ffd4a3 100%);
          border-radius: 0 0 6px 6px;
          box-shadow: inset 0 1px 2px rgba(255,255,255,0.3);
        }
        
        .clothing {
          position: relative;
          width: 45px;
          height: 28px;
          background: ${alsokTheme.primary.main};
          border-radius: 8px 8px 0 0;
        }
        
        .uniform {
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, ${alsokTheme.primary.main} 0%, ${alsokTheme.primary.light} 50%, ${alsokTheme.primary.main} 100%);
          border-radius: 8px 8px 0 0;
          box-shadow: inset 0 2px 4px rgba(255,255,255,0.2), inset 0 -1px 2px rgba(0,0,0,0.1);
        }
        
        .uniform::before {
          content: '';
          position: absolute;
          top: 6px;
          left: 50%;
          transform: translateX(-50%);
          width: 4px;
          height: 14px;
          background: rgba(255,255,255,0.4);
          border-radius: 2px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.2);
        }
        
        .uniform::after {
          content: '';
          position: absolute;
          top: 4px;
          left: 8px;
          width: 6px;
          height: 6px;
          background: rgba(255,255,255,0.2);
          border-radius: 50%;
          box-shadow: 24px 0 0 rgba(255,255,255,0.2);
        }
        
        .name-tag {
          position: absolute;
          top: 4px;
          right: 4px;
          background: linear-gradient(135deg, #ffffff 0%, #f8f8f8 100%);
          color: ${alsokTheme.primary.main};
          font-size: 6px;
          font-weight: bold;
          padding: 2px 4px;
          border-radius: 3px;
          border: 1px solid ${alsokTheme.primary.light};
          box-shadow: 0 2px 4px rgba(0,0,0,0.2), inset 0 1px 2px rgba(255,255,255,0.8);
          font-family: 'Arial', sans-serif;
        }
        
        .avatar-pulse {
          position: absolute;
          top: -15px;
          left: -15px;
          right: -15px;
          bottom: -15px;
          border: 3px solid ${alsokTheme.primary.light};
          border-radius: ${alsokTheme.borderRadius.full};
          opacity: 0;
        }
        
        .avatar-listening .avatar-pulse {
          animation: ring 1.5s ease-out infinite;
        }
        
        .avatar-message-overlay {
          position: absolute;
          top: 15px;
          right: -20px;
          max-width: 140px;
          z-index: 10;
          animation: fadeInScale 0.3s ease-out;
        }
        
        .message-bubble {
          background: rgba(255, 255, 255, 0.96);
          padding: 8px 12px;
          border-radius: 12px;
          box-shadow: 0 3px 15px rgba(0, 0, 0, 0.2);
          position: relative;
          font-size: 10px;
          line-height: 1.3;
          color: ${alsokTheme.primary.main};
          border: 1.5px solid ${alsokTheme.primary.light};
          font-family: ${alsokTheme.typography.fontFamily.primary};
          font-weight: ${alsokTheme.typography.fontWeight.medium};
          backdrop-filter: blur(8px);
          text-align: left;
          min-height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          word-wrap: break-word;
          hyphens: auto;
        }
        
        .message-bubble::before {
          content: '';
          position: absolute;
          left: -6px;
          top: 12px;
          width: 0;
          height: 0;
          border-top: 6px solid transparent;
          border-bottom: 6px solid transparent;
          border-right: 6px solid rgba(255, 255, 255, 0.96);
        }
        
        .message-bubble::after {
          content: '';
          position: absolute;
          left: -8px;
          top: 11px;
          width: 0;
          height: 0;
          border-top: 7px solid transparent;
          border-bottom: 7px solid transparent;
          border-right: 7px solid ${alsokTheme.primary.light};
          z-index: -1;
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        @keyframes bounce {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes blink {
          0%, 90%, 100% { 
            height: 8px;
            opacity: 1; 
          }
          95% { 
            height: 1px;
            opacity: 0.8; 
          }
        }
        
        @keyframes listening-eyes {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-1px); }
          75% { transform: translateX(1px); }
        }
        
        @keyframes speaking-mouth {
          0%, 100% { 
            width: 8px;
            height: 4px;
            border-radius: 0 0 8px 8px;
          }
          25% { 
            width: 10px;
            height: 8px;
            border-radius: 50%;
          }
          50% { 
            width: 12px;
            height: 6px;
            border-radius: 0 0 12px 12px;
          }
          75% { 
            width: 6px;
            height: 3px;
            border-radius: 0 0 6px 6px;
          }
        }
        
        @keyframes gentle-nod {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-1px) rotate(1deg); }
        }
        
        @keyframes ring {
          0% {
            opacity: 0;
            transform: scale(1);
          }
          50% {
            opacity: 1;
          }
          100% {
            opacity: 0;
            transform: scale(1.3);
          }
        }
        
        @keyframes fadeInScale {
          0% {
            opacity: 0;
            transform: scale(0.8) translateY(10px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </div>
  )
})

Avatar.displayName = 'Avatar'

export default Avatar